import { Injectable } from '@angular/core'
import { I18nRequest, I18nResponse } from '@interfaces/i-i18n'
import { TranslationService } from '@services/translation/translation.service'
import { logger } from '@utils/logger'
import { ReplaySubject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

/**
 * @description Provides a cacheing service for the inlineTranslationDirective. As this directive may have a large number of
 * calls per page load, it would not be advisable to be making an http translation request for each one. This service will
 * cache all of the recieved requests for 100ms before making a single translation request with an array.
 * @see inline-translation.directive.ts
 */

@Injectable({
  providedIn: 'root',
})
export class TranslationCacheService {
  private translationResponseCache = new ReplaySubject<I18nResponse>()
  private translationRequestCache = new ReplaySubject<I18nRequest[]>()
  private requestCache: I18nRequest[] = []

  /**
   * @description emits the translation service response
   */
  translationCache$ = this.translationResponseCache.asObservable()

  /**
   * @description Debounce the requested translation method, only making a call to the translation service with all recieved values after 100ms
   * @param {TranslationService} translationService
   */
  constructor(private translationService: TranslationService) {
    this.translationRequestCache.pipe(debounceTime(100)).subscribe({
      next: (res: I18nRequest[]) => this.requestTranslationPacket(res),
      error: (err: any) => logger('error', err),
    })
  }

  /**
   * @description Request a translation
   * @param {I18nRequest} request
   */
  requestTranslation(request: I18nRequest): void {
    this.requestCache.push(request)
    this.translationRequestCache.next(this.requestCache)
  }

  /**
   * @description Requests multiple translations
   * @param requests
   * */
  requestTranslations(requests: I18nRequest[]): void {
    requests.forEach((request) => {
      this.requestCache.push(request)
    })
    this.translationRequestCache.next(this.requestCache)
  }

  /**
   * @description Makes the request to the translation service with cached requests
   */
  private requestTranslationPacket(request: I18nRequest[]): void {
    this.translationService.getTranslation(request).subscribe({
      next: (translationPacket: I18nResponse) => this.translationResponseCache.next(translationPacket),
      error: (err: any) => logger('error', err),
    })
    this.requestCache = []
  }
}
